import React, { FC, useEffect, useReducer, useRef } from 'react';

import {
  CloudAccountGetResponse,
  CloudProviders,
} from '@ariksa/cloud-account/api';
import { HStack } from '@chakra-ui/layout';
import { map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import {
  CancelButton,
  DocumentsIcon,
  getIcon,
  InformationAlert,
  Modal,
  OutlinedForm,
  PrimaryButton,
  StackedHeader,
} from 'components/index';
import { selectCloudAccounts } from 'containers/Setup/CloudAccounts/selectors';
import { actions } from 'containers/Setup/CloudAccounts/slice';
import { selectSharedState } from 'containers/SharedState/selectors';

import { useCloudAccountId } from '../../utils';
import { useCloudAccountsFunctions } from '../hooks/useCloudAccountsFunctions';

interface Props {
  onClose();
  isOpen: boolean;
  row: CloudAccountGetResponse;
}

export const ScanByOutpost: FC<Props> = props => {
  const { onClose, isOpen, row } = props;
  const { dataScannerInfo } = useSelector(selectSharedState);
  const { apiCallStatus } = useSelector(selectCloudAccounts);
  const { accountMapping } = useCloudAccountId();
  const dispatch = useDispatch();
  const { getCloudAccounts } = useCloudAccountsFunctions();
  const okButtonRef = useRef<HTMLButtonElement>(null);
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      outpostAccountOptions: [],
      outpostAccount: {},
      regionOptions: [],
      region: {},
      outpostInstanceId: '',
    },
  );

  //set outpost account options
  useEffect(() => {
    const dataOutpostOptions = map(
      dataScannerInfo.data?.scanners,
      (scanners, id) => {
        const activeScanners = scanners?.filter(
          scanner => scanner?.status === 'Active',
        );

        if (!activeScanners?.length) return null;

        return {
          label: accountMapping?.[id]?.name,
          value: id,
          data: activeScanners,
          icon: getIcon(accountMapping?.[id]?.cloud_type!),
        };
      },
    ).filter(Boolean);

    updateState({
      outpostAccountOptions: dataOutpostOptions,
      outpostAccount: dataOutpostOptions[0],
    });
  }, [dataScannerInfo.data, accountMapping]);

  //set region options
  useEffect(() => {
    if (!state?.outpostAccount) return;
    const items = map(state.outpostAccount?.data, o => ({
      label: o.region,
      value: o.region,
      data: o,
    }));

    updateState({
      regionOptions: items,
      region: items[0],
      outpostInstanceId: items[0]?.data?.instance_id,
    });
  }, [state?.outpostAccount]);

  const handleSubmit = () => {
    dispatch(
      actions.addDataOutpostForRepository({
        q: {
          accountId: row.uuid!,
          nonCloudScannerAssignmentUpdate: {
            scanner_instance_id: state?.outpostInstanceId,
          },
        },
        onSuccess: () => {
          getCloudAccounts();
          onClose();
        },
      }),
    );
  };
  const isSubmitDisabled =
    !state.outpostAccount || !state.region || !state.outpostInstanceId;

  useEffect(() => {
    if (!isOpen || isSubmitDisabled || dataScannerInfo.isLoading) return;

    okButtonRef.current?.focus();
  }, [dataScannerInfo.isLoading, isOpen, isSubmitDisabled]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={okButtonRef}
      header={
        <StackedHeader
          upper="Enable Scan By Outpost"
          lower={
            'Detect sensitive data in ' +
            (row.cloud_type === CloudProviders.Snowflake
              ? 'databases'
              : 'code repositories')
          }
          icon={getIcon(row.cloud_type!)}
          iconFilled={false}
          border
        />
      }
      size="xl"
      body={
        <OutlinedForm
          fields={[
            {
              type: 'custom',
              name: 'disclaimer',
              component: (
                <InformationAlert
                  icon={<DocumentsIcon color="primary" />}
                  description="Ariksa will automatically scan all repositories and branches that are
            accessible under this account"
                />
              ),
            },
            {
              type: 'text',
              name: 'account',
              label: 'Account',
              value: row?.name,
              inputProps: {
                isReadOnly: true,
              },
            },
            {
              type: 'select',
              name: 'outpostAccount',
              label: 'Outpost Account',
              options: state.outpostAccountOptions,
              value: state.outpostAccount,
              isLoading: dataScannerInfo.isLoading,
              onChange: outpostAccount => updateState({ outpostAccount }),
            },
            {
              type: 'select',
              name: 'region',
              label: 'Preferred Region',
              options: state.regionOptions,
              value: state.region,
              isLoading: dataScannerInfo.isLoading,
              onChange: region =>
                updateState({
                  region,
                  outpostInstanceId: region?.data?.instance_id,
                }),
            },
            {
              type: 'text',
              name: 'dataOutpost',
              label: 'Assign to Data Outpost',
              isLoading: dataScannerInfo.isLoading,
              inputProps: {
                isReadOnly: true,
              },
              value: state?.outpostInstanceId,
            },
          ]}
        />
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton onClick={onClose} />
          <PrimaryButton
            ref={okButtonRef}
            px={6}
            onClick={handleSubmit}
            isDisabled={isSubmitDisabled}
            isLoading={dataScannerInfo.isLoading || apiCallStatus.isLoading}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
    />
  );
};
